<template>
  <template v-if="loading">
    <div class="px-6 pt-6 pb-4">
      <div class="h-3 w-48 bg-gray-200 rounded animate-pulse mb-4"></div>
      <div class="h-6 w-64 bg-gray-200 rounded animate-pulse mb-6"></div>
      <div class="space-y-3">
        <div class="h-4 w-72 bg-gray-200 rounded animate-pulse"></div>
        <div class="h-4 w-56 bg-gray-200 rounded animate-pulse"></div>
        <div class="flex items-center space-x-2">
          <div class="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-6 w-36 bg-gray-100 rounded-full animate-pulse"></div>
        </div>
        <div class="flex items-center space-x-2">
          <div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
          <div class="h-6 w-24 bg-gray-100 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
    <div class="p-6">
      <div class="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div class="h-5 w-24 bg-gray-200 rounded animate-pulse mb-4"></div>
        <div class="space-y-2">
          <div v-for="n in 5" :key="'sk-row-' + n" class="h-4 bg-gray-100 rounded animate-pulse" :style="{ width: (60 + n * 5) + '%' }"></div>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
  <PageHeader
    :title="user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'User Details'"
    :breadcrumbs="[{ label: 'IAM Console', to: '/tenantadmin/iam-console' }, { label: 'User Information' }]"
  >
    <template v-if="canEditUserName" #title-suffix>
      <button
        class="name-edit-btn"
        title="Edit user name"
        @click="openNameEdit"
      >
        <font-awesome-icon icon="fa-solid fa-pencil" />
      </button>
    </template>
    <div><b>Email:</b> {{ user.email }}</div>
    <div class="flex items-center flex-wrap">
      <b>Access Enabled:</b>
      <button
        v-if="canToggleActive"
        type="button"
        class="status-toggle ml-2"
        :class="user.is_active ? 'status-toggle--active' : 'status-toggle--inactive'"
        :disabled="togglingActive"
        :aria-pressed="user.is_active"
        :aria-label="user.is_active ? 'Deactivate user' : 'Activate user'"
        @click="toggleActive"
      >
        <span class="status-toggle__track" aria-hidden="true">
          <span class="status-toggle__thumb"></span>
        </span>
        {{ togglingActive ? 'Updating...' : user.is_active ? 'Active' : 'Inactive' }}
      </button>
      <span
        v-else
        class="status-badge ml-2"
        :class="user.is_active ? 'status-badge--active' : 'status-badge--inactive'"
      >
        {{ user.is_active ? 'Active' : 'Inactive' }}
      </span>
      <span v-if="activeStatusError" class="ml-2 text-sm text-red-600">
        {{ activeStatusError }}
      </span>
    </div>
    <div class="flex items-center flex-wrap">
      <b class="mr-2">Access Role(s):</b>
      <span class="flex flex-wrap items-center">
        <TagBadge_V2
          v-for="role in userAccessRoleNames"
          :key="role"
          :text="role"
        />
      </span>
      <button
        v-if="canEditAccessRoles"
        class="edit-btn ml-2"
        @click="openModal('accessRoles')"
      >
        <font-awesome-icon icon="fa-solid fa-pencil" class="mr-1" />Edit
      </button>
    </div>
    <div class="flex items-center flex-wrap">
      <b class="mr-2">Portal Role(s):</b>
      <span class="flex flex-wrap items-center">
        <TagBadge_V2
          v-for="role in user.portal_roles"
          :key="role.id"
          :text="role.role_name"
        />
      </span>
      <button
        v-if="canEditAccessRoles"
        class="edit-btn ml-2"
        @click="openModal('portalRoles')"
      >
        <font-awesome-icon icon="fa-solid fa-pencil" class="mr-1" />Edit
      </button>
    </div>
    <div class="flex items-center flex-wrap">
      <b class="mr-2">Provider Messaging:</b>
      <template v-if="mailboxStatus === 'active'">
        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Mailbox Active
        </span>
        <button
          v-if="canManageMailbox"
          class="btn-secondary ml-2 text-sm"
          :disabled="deactivatingMailbox"
          @click="handleDeactivateMailbox"
        >
          {{ deactivatingMailbox ? 'Deactivating...' : 'Deactivate Mailbox' }}
        </button>
      </template>
      <template v-else-if="mailboxStatus === 'inactive'">
        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          Mailbox Inactive
        </span>
        <button
          v-if="canManageMailbox"
          class="btn-primary ml-2 text-sm"
          :disabled="reactivatingMailbox"
          @click="handleReactivateMailbox"
        >
          {{ reactivatingMailbox ? 'Reactivating...' : 'Reactivate Mailbox' }}
        </button>
      </template>
      <template v-else-if="mailboxStatus === 'practice_verification_owner'">
        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          Practice Verification Owner
        </span>
      </template>
      <template v-else>
        <span class="text-sm text-gray-500">No mailbox</span>
        <button
          v-if="canManageMailbox"
          class="btn-primary ml-2 text-sm"
          :disabled="provisioningMailbox"
          @click="handleProvisionMailbox"
        >
          {{ provisioningMailbox ? 'Provisioning...' : 'Provision Mailbox' }}
        </button>
      </template>
      <span v-if="mailboxActionError" class="ml-2 text-sm text-red-600">
        {{ mailboxActionError }}
      </span>
    </div>

  </PageHeader>

  <div class="p-6">
    <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <ComponentHeader title="Entities">
        <div class="flex items-center gap-1">
          <button
            v-if="selectedEntitiesForRemoval.length > 0"
            @click="bulkRemoveEntities"
            class="btn-danger-sm"
          >
            <FontAwesomeIcon :icon="['fas', 'trash']" />
            Remove Selected ({{ selectedEntitiesForRemoval.length }})
          </button>
          <EntitySearch
            @entities-selected="selected => onAssignEntities(selected)"
            :allow-descendant-selection="false"
            :clear-selection-on-close="true"
          />
        </div>
      </ComponentHeader>
      <AgGridVue 
        :theme="standardListTheme"
        style="width: 100%; height: 500px"
        :column-defs="entityColumnDefs"
        :row-data="portalEntities"
        :grid-options="gridOptions"
        :row-selection="rowSelection"
        :suppress-row-click-selection="true"
        @selection-changed="onSelectionChanged"
        :loading="loading"
        :default-col-def="{ flex: 1, sortable: true, filter: true }"
        @grid-ready="onGridReady"
      />
    </div>
  </div>

  <!-- Modal for editing access roles -->
  <div
    v-if="modalStates.accessRoles"
    class="modal-overlay"
    @click.self="closeModal('accessRoles')"
  >
    <div class="modal-content">
      <button class="modal-close" @click="closeModal('accessRoles')">
        &times;
      </button>
      <div>
        <h3 class="text-lg font-semibold mb-4">Edit Access Roles</h3>
        <Dropdown_Selector_Multiple
          :options="availableAccessRoles"
          :initial="initialAccessRoles"
          :applyDisabled="isSaving.accessRoles"
          class="mb-4"
          @submit="selected => saveModal('accessRoles', selected)"
        />
        <div v-if="accessRolesError" style="color: #c00; margin-top: 8px;">
          {{ accessRolesError }}
        </div>
        <div v-if="saveError" class="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {{ saveError }}
        </div>
      </div>
    </div>
  </div>

  <!-- Modal for editing portal roles -->
  <div
    v-if="modalStates.portalRoles"
    class="modal-overlay"
    @click.self="closeModal('portalRoles')"
  >
    <div class="modal-content">
      <button class="modal-close" @click="closeModal('portalRoles')">
        &times;
      </button>
      <div>
        <h3 class="text-lg font-semibold mb-4">Edit Portal Roles</h3>
        <Dropdown_Selector_Multiple
          :options="allPortalRoles"
          :initial="initialPortalRoles"
          :applyDisabled="isSaving.portalRoles"
          class="mb-4"
          @submit="selected => saveModal('portalRoles', selected)"
        />
        <div v-if="saveError" class="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {{ saveError }}
        </div>
      </div>
    </div>
  </div>

  <!-- Modal for editing user name -->
  <div
    v-if="modalStates.userName"
    class="modal-overlay"
    @click.self="closeModal('userName')"
  >
    <div class="modal-content">
      <button class="modal-close" @click="closeModal('userName')">
        &times;
      </button>
      <div>
        <h3 class="text-lg font-semibold mb-4">Edit User Name</h3>
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            v-model="editName.firstName"
            type="text"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            v-model="editName.lastName"
            type="text"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div v-if="nameError" class="text-red-600 text-sm mb-3">
          {{ nameError }}
        </div>
        <div class="flex justify-end gap-2">
          <button
            class="btn-secondary"
            @click="closeModal('userName')"
          >
            Cancel
          </button>
          <button
            class="btn-primary"
            :disabled="isSaving.userName"
            @click="saveName"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>

  </template>
</template>

<script>
import axios from 'axios';
import { AgGridVue } from 'ag-grid-vue3';
import TagBadge_V2 from '@/components/widgetcomponents/TagBadge_V2.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Dropdown_Selector_Multiple from '../widgetcomponents/Dropdown_Selector/Dropdown_Selector_Multiple.vue';
import { mapGetters } from 'vuex';
import { standardListTheme } from '@/utils/common';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import EntitySearch from '../widgetcomponents/Search_Widgets/EntitySearch_V2.vue';
import CC_Router_Link_To_Entity from '../widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import {
  deactivateMailbox,
  errorMessage,
  provisionMailbox,
  reactivateMailbox,
} from '@/services/api/providerMessaging';
import {
  getUser,
  updateUser,
  batchUpdateAccessRoles,
  batchUpdatePortalRoles,
  batchUpdateEntities,
} from '@/services/api/adminUsers';

export default {
  components: {
    AgGridVue,
    TagBadge_V2,
    Dropdown_Selector_Multiple,
    FontAwesomeIcon,
    EntitySearch,
    PageHeader,
    ComponentHeader
  },
  props: {
    id: {
      type: [String],
      required: true
    }
  },
  data() {
    return {
      user: {},
      loading: true,
      gridOptions: {
        animateRows: true,
        statusBar: {
          statusPanels: [
            { statusPanel: 'agTotalAndFilteredRowCountComponent' },
          ],
        },
      },
      entityColumnDefs: [
        {
          headerName: 'Entity Name',
          field: 'name',
          cellRenderer: CC_Router_Link_To_Entity,
          cellRendererParams: () => ({
            dataType: 'entity',
          }),
          sort: 'asc',
        },
        {
          headerName: 'Entity Type',
          field: 'entity_type_name',
        },
      ],
      modalStates: {
        accessRoles: false,
        portalRoles: false,
        portalEntities: false,
        userName: false,
      },
      isSaving: {
        accessRoles: false,
        portalRoles: false,
        portalEntities: false,
        userName: false,
      },
      editName: {
        firstName: '',
        lastName: '',
      },
      nameError: '',
      allPortalRoles: [],
      accessRolesError: '',
      saveError: '',
      rowSelection: {
        mode: 'multiRow',
        checkboxes: true
      },
      selectedEntitiesForRemoval: [],
      mailboxStatus: null,
      provisioningMailbox: false,
      deactivatingMailbox: false,
      reactivatingMailbox: false,
      mailboxActionError: '',
      togglingActive: false,
      activeStatusError: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    canManageMailbox() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE)
        && canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING)
        && this.mailboxStatus !== 'practice_verification_owner'
    },
    ...mapGetters('featureConfig', ['roles']),
    standardListTheme() {
      return standardListTheme;
    },
    userAccessRoleNames() {
      return this.user.access_roles?.map(roleId => {
        const found = this.roles.find(r => r.role_id === roleId);
        return found ? found.display_name : roleId;
      });
    },
    initialAccessRoles() {
      return this.user.access_roles?.slice();
    },
    initialPortalRoles() {
      return this.user.portal_roles?.map(r => r.role);
    },
    initialEntities() {
      return this.user.entity_access?.map(e => e.id);
    },
    portalEntities() {
      return this.user.entity_access?.map(entity => ({
        ...entity,
        entity_id: entity.id,
        entity_type_name: entity.entity_type_name || '',
      }));
    },
    availableAccessRoles() {
      return this.roles
        .filter(r => !r.hide_from_config)
        .map(r => ({ id: r.role_id, name: r.display_name }));
    },
    canEditAccessRoles() {
      return canAccessFeature(FEATURES.ACTION_USER_EDIT_ROLES, { superuserOverride: false });
    },
    canEditUserName() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE, { superuserOverride: false });
    },
    canToggleActive() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE, { superuserOverride: false })
        && this.user.id !== this.current_user?.id;
    }
  },
  async created() {
    this.loading = true;
    this.$store.dispatch('featureConfig/fetchConfig');
    const [, portalRolesResp] = await Promise.all([
      this.fetchUser(),
      axios.get('/api/portalroles/'),
    ]);
    this.allPortalRoles = portalRolesResp.data;
    this.loading = false;
  },
  watch: {
    current_tenant_slug() {
      this.$store.dispatch('featureConfig/fetchConfig');
    },
  },
  methods: {
    async fetchUser() {
      this.user = await getUser(this.id);
      this.mailboxStatus = this.user.provider_mailbox_status;
    },
    setMailboxStatus(mailbox) {
      if (mailbox?.status !== 'active' && mailbox?.status !== 'inactive') return;

      this.mailboxStatus = mailbox.status;
    },
    async toggleActive() {
      this.togglingActive = true;
      this.activeStatusError = '';
      try {
        await updateUser(this.user.id, { is_active: !this.user.is_active });
        await this.fetchUser();
      } catch (error) {
        this.activeStatusError = error.response?.status === 403
          ? 'You do not have permission to change this user\'s access.'
          : 'Failed to update user access. Please try again.';
        console.error('Failed to toggle user status:', error);
      } finally {
        this.togglingActive = false;
      }
    },
    openModal(key) {
      this.modalStates[key] = true;
    },
    closeModal(key) {
      this.modalStates[key] = false;
    },
    async saveModal(key, selected) {
      this.isSaving[key] = true;
      this.saveError = '';
      try {
        if (key === 'accessRoles') {
          if (!selected.length) {
            this.accessRolesError = 'At least one access role is required.';
            return;
          }
          this.accessRolesError = '';
          await batchUpdateAccessRoles(this.user.id, selected);
        } else if (key === 'portalRoles') {
          await batchUpdatePortalRoles(this.user.id, selected);
        } else if (key === 'entities') {
          await batchUpdateEntities(this.user.id, selected);
        }
        await this.fetchUser();
        this.closeModal(key);
      } catch (error) {
        if (error.response?.status === 403) {
          this.saveError = 'You do not have permission to perform this action.';
        } else {
          this.saveError = 'Failed to save changes. Please try again.';
        }
      } finally {
        this.isSaving[key] = false;
      }
    },
    onGridReady(params) {
      this.gridApi = params.api;
    },
    onAssignEntities(entities) {
      const updatedEntities = [...new Set([...this.initialEntities, ...entities])];
      this.saveModal('entities', updatedEntities);
    },
    onSelectionChanged() {
      if (this.gridApi) {
        const selectedNodes = this.gridApi.getSelectedNodes();
        this.selectedEntitiesForRemoval = selectedNodes.map(node => node.data);
      }
    },
    openNameEdit() {
      this.editName.firstName = this.user.first_name || '';
      this.editName.lastName = this.user.last_name || '';
      this.nameError = '';
      this.openModal('userName');
    },
    async saveName() {
      if (!this.editName.firstName.trim() || !this.editName.lastName.trim()) {
        this.nameError = 'First name and last name are required.';
        return;
      }
      this.isSaving.userName = true;
      try {
        await updateUser(this.user.id, {
          first_name: this.editName.firstName.trim(),
          last_name: this.editName.lastName.trim(),
        });
        await this.fetchUser();
        this.closeModal('userName');
      } catch (error) {
        if (error.response?.status === 403) {
          this.nameError = 'You do not have permission to update this user\'s name.';
        } else {
          this.nameError = 'Failed to update user name.';
        }
      } finally {
        this.isSaving.userName = false;
      }
    },
    async handleProvisionMailbox() {
      this.mailboxActionError = ''
      this.provisioningMailbox = true
      try {
        const mailbox = await provisionMailbox(this.user.id)
        this.setMailboxStatus(mailbox)
      } catch (e) {
        const code = e?.response?.data?.code
        if (e?.response?.status === 409 && code === 'mailbox_already_active') {
          this.mailboxStatus = 'active'
        } else if (e?.response?.status === 409 && code === 'mailbox_already_inactive') {
          this.mailboxStatus = 'inactive'
        } else if (e?.response?.status === 409) {
          await this.fetchUser()
        } else {
          console.error('Failed to provision mailbox:', e)
          this.mailboxActionError = errorMessage(e)
        }
      } finally {
        this.provisioningMailbox = false
      }
    },
    async handleDeactivateMailbox() {
      this.mailboxActionError = ''
      this.deactivatingMailbox = true
      try {
        const mailbox = await deactivateMailbox(this.user.id)
        this.setMailboxStatus(mailbox)
      } catch (e) {
        console.error('Failed to deactivate mailbox:', e)
        this.mailboxActionError = e?.response?.data?.code === 'verified_mailbox_cannot_be_deactivated'
          ? 'This is an Updox signatory user and cannot be deactivated. Contact support@kaironhealth.com for assistance.'
          : errorMessage(e)
        if (e?.response?.status === 409 && e?.response?.data?.code === 'mailbox_already_inactive') {
          this.mailboxStatus = 'inactive'
          this.mailboxActionError = ''
        } else if (e?.response?.status === 409) {
          await this.fetchUser()
        }
      } finally {
        this.deactivatingMailbox = false
      }
    },
    async handleReactivateMailbox() {
      this.mailboxActionError = ''
      this.reactivatingMailbox = true
      try {
        const mailbox = await reactivateMailbox(this.user.id)
        this.setMailboxStatus(mailbox)
      } catch (e) {
        const code = e?.response?.data?.code

        if (e?.response?.status === 404 && code === 'mailbox_not_found') {
          // The backend is authoritative: the local mailbox record is gone.
          // Refresh the rest of the user's row, then offer provisioning.
          await this.fetchUser()
        } else if (e?.response?.status === 409 && code === 'mailbox_already_active') {
          // The page was stale; the mailbox is already active.
          this.mailboxStatus = 'active'
        } else {
          // In particular, updox_reactivation_failed leaves the mailbox
          // inactive and allows the user to retry.
          console.error('Failed to reactivate mailbox:', e)
          this.mailboxActionError = errorMessage(e)
        }
      } finally {
        this.reactivatingMailbox = false
      }
    },
    bulkRemoveEntities() {
      if (this.selectedEntitiesForRemoval.length === 0) {
        return;
      }
      const entityIdsToRemove = this.selectedEntitiesForRemoval.map(entity => entity.id);
      const updatedEntities = this.initialEntities.filter(id => !entityIdsToRemove.includes(id));
      this.saveModal('entities', updatedEntities);
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 90vw;
  max-width: 500px;
  position: relative;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.2);
}
.modal-close {
  position: absolute;
  top: 8px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
}
.name-edit-btn {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 0.85em;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
  padding: 2px 6px;
}
.name-edit-btn:hover {
  color: #ff7a1a;
}
:deep(.title-row):hover .name-edit-btn {
  opacity: 1;
}
.edit-btn,
.assign-btn {
  background: #ff7a1a;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 2px 10px;
  margin-left: 8px;
  cursor: pointer;
  font-size: 0.95em;
}
.edit-btn:hover,
.assign-btn:hover {
  background: #e55d00;
}
.status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  border-radius: 9999px;
  padding: 0.25rem 0.6rem 0.25rem 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}
.status-toggle:disabled {
  cursor: wait;
  opacity: 0.65;
}
.status-toggle--active,
.status-badge--active {
  background: #dcfce7;
  color: #166534;
}
.status-toggle--inactive,
.status-badge--inactive {
  background: #fee2e2;
  color: #991b1b;
}
.status-toggle__track {
  display: inline-flex;
  align-items: center;
  width: 1.75rem;
  height: 1rem;
  padding: 0.125rem;
  border-radius: 9999px;
  background: currentColor;
}
.status-toggle__thumb {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  background: #fff;
  transition: transform 0.15s ease;
}
.status-toggle--active .status-toggle__thumb {
  transform: translateX(0.75rem);
}
.status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
}
</style>
